import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { login } from '../contexts/db.User';
import { checkSubscriptionAccess } from '../contexts/db.Subscription';
import ErrorModal from '../components/ErrorModal';
import '../styles/pages/Auth.css';

function Login({ language }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const t = translations[language];
  const navigate = useNavigate();

  // Validate email format
  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    if (!email) {
      errors.email = t.requiredField;
    } else if (!isValidEmail(email)) {
      errors.email = t.invalidEmail;
    }
    
    if (!password) {
      errors.password = t.requiredField;
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time validation for individual fields
  const validateField = (field, value) => {
    const errors = { ...fieldErrors };
    
    switch (field) {
      case 'email':
        if (!value) {
          errors.email = t.requiredField;
        } else if (!isValidEmail(value)) {
          errors.email = t.invalidEmail;
        } else {
          delete errors.email;
        }
        break;
      case 'password':
        if (!value) {
          errors.password = t.requiredField;
        } else {
          delete errors.password;
        }
        break;
    }
    
    setFieldErrors(errors);
  };

  // Handle field blur to mark as touched
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({ email: true, password: true });
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await login(email, password);
      
      // Get user ID for subscription check
      const userId = response.user?.id || response.id || 'unknown';
      console.log('Login page - Extracted userId:', userId);
      
      // Store token if returned by Laravel API
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }
      
      // Store user data
      if (response.user) {
        localStorage.setItem('userData', JSON.stringify(response.user));
      } else {
        // Store basic user data if user object is not returned
        const userData = {
          id: userId,
          email: email,
          first_name: response.first_name || response.name || 'User'
        };
        localStorage.setItem('userData', JSON.stringify(userData));
      }
      
      // Check subscription status
      if (userId !== 'unknown') {
        try {
          const subscriptionCheck = await checkSubscriptionAccess(userId);
          
          if (subscriptionCheck.hasAccess) {
            // User has valid subscription, redirect to home
            navigate('/');
          } else {
            // User needs subscription, redirect to subscription page
            navigate('/subscription', { 
              state: { 
                userId: userId,
                subscriptionStatus: subscriptionCheck.status,
                fromLogin: true
              } 
            });
          }
        } catch (subscriptionError) {
          console.error('Subscription check error:', subscriptionError);
          // On subscription check error, still allow login
          navigate('/');
        }
      } else {
        // User ID is unknown, proceed to home without subscription check
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle Laravel authentication errors
      let errorMessage;
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage = t.loginError || 'Login failed. Please try again.';
      }
      
      setError(errorMessage);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="auth-container">
      <div className="auth-form-container">
        <div className="auth-header">
          <h1>{t.login}</h1>
          <p className="auth-subtitle">{t.welcomeBack}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (touched.email) validateField('email', e.target.value);
              }}
              onBlur={() => {
                handleBlur('email');
                validateField('email', email);
              }}
              placeholder={t.enterEmail}
              className={fieldErrors.email && touched.email ? 'input-error' : ''}
              required
            />
            {fieldErrors.email && touched.email && (
              <div className="field-error">{fieldErrors.email}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">{t.password}</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (touched.password) validateField('password', e.target.value);
                }}
                onBlur={() => {
                  handleBlur('password');
                  validateField('password', password);
                }}
                placeholder={t.enterPassword}
                className={fieldErrors.password && touched.password ? 'input-error' : ''}
                required
              />
              <button 
                type="button" 
                className="toggle-password-button"
                onClick={togglePassword}
                aria-label={showPassword ? t.hidePassword : t.showPassword}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && touched.password && (
              <div className="field-error">{fieldErrors.password}</div>
            )}
          </div>

          <div className="form-links">
            <Link to="/forgot-password" className="forgot-password-link">
              {t.forgotPassword}
            </Link>
          </div>

          <button type="submit" className="auth-submit-button" disabled={loading}>
            {loading ? t.signingIn : t.login}
          </button>
        </form>

        <div className="auth-alt-action">
          <p>
            {t.noAccount}{' '}
            <Link to="/register" className="auth-link">
              {t.signUp}
            </Link>
          </p>
        </div>
      </div>
      
      <ErrorModal
        isOpen={showErrorModal}
        message={error}
        title={t.error}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
}

export default Login;
